// CompanySelection.tsx
import React, {useEffect, useState, useMemo} from 'react';
import axios from 'axios';
import {ColumnDef} from '@tanstack/react-table';
import {DataGrid, KeenIcon} from '@/components';
import {Link} from 'react-router-dom'; // Import Link component
import {CompanyDetailsData} from '@/pages/buddy/types/CompanyDetailsData';
import {enqueueSnackbar} from "notistack";

interface CompanySelectionProps {
    onSelectCompany: (company: CompanyDetailsData) => void;
}

const CompanySelection: React.FC<CompanySelectionProps> = ({onSelectCompany}) => {
    // State to store the companies data and loading/error state
    const [data, setData] = useState<CompanyDetailsData[]>([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');

    // Define the API base URL dynamically from environment variables
    const API_COMPANIES = `${import.meta.env.VITE_APP_API_URL}/companies`;

    const fetchCompaniesData = async () => {
        try {
            const response = await axios.get<CompanyDetailsData[]>(API_COMPANIES, {
                headers: {
                    Accept: 'application/json',
                },
            });
            setData(response.data);
        } catch (err) {
            enqueueSnackbar(`Failed to fetch companies data: ${err}`, {
                variant: 'solid',
                state: 'danger'
            });
        } finally {
            setLoading(false);
        }
    };

    // Fetch data on component mount
    useEffect(() => {
        fetchCompaniesData();
    }, []);

    const columns = useMemo<ColumnDef<CompanyDetailsData>[]>(
        () => [
            {
                accessorFn: (row) => row.name,
                id: 'name',
                header: () => 'Company',
                enableSorting: true,
                cell: (info) => (
                    <div className="flex flex-col gap-2">
                        <Link
                            className="leading-none font-medium text-sm text-gray-900 hover:text-primary"
                            to="#"
                            onClick={(e) => {
                                e.preventDefault(); // Prevent default link behavior
                                onSelectCompany(info.row.original); // Call the selection function
                            }}
                        >
                            {info.row.original.name}
                        </Link>
                    </div>
                ),
                meta: {
                    className: 'min-w-[250px]',
                    cellClassName: 'text-gray-700 font-normal',
                },
            },
            {
                id: 'select',
                header: () => '',
                enableSorting: false,
                cell: ({row}) => (
                    <button
                        className="btn btn-sm btn-icon btn-clear btn-light"
                        onClick={() => onSelectCompany(row.original)}
                    >
                        <KeenIcon icon="check-circle" className="text-primary cursor-pointer"/>
                    </button>
                ),
                meta: {
                    className: 'w-[50px]',
                },
            },
        ],
        [onSelectCompany]
    );

    // Filtered data based on search term
    const filteredData = useMemo(() => {
        return data.filter((company) =>
            company.name.toLowerCase().includes(searchTerm.toLowerCase())
        );
    }, [searchTerm, data]);

    if (loading) return <div>Loading companies...</div>;


    return (
        <div className="card card-grid min-w-full">
            <div className="card-header flex-wrap py-5">
                <h3 className="card-title">Please select a company to associate with the new job</h3>
                <div className="flex gap-6">
                    <div className="relative">
                        <KeenIcon
                            icon="magnifier"
                            className="leading-none text-md text-gray-500 absolute top-1/2 left-0 -translate-y-1/2 ml-3"
                        />
                        <input
                            type="text"
                            placeholder="Search Companies"
                            className="input input-sm pl-8"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                    </div>
                </div>
            </div>
            <div className="card-body">
                <DataGrid
                    columns={columns}
                    data={filteredData}
                    pagination={{size: 10}}
                    sorting={[{id: 'name', desc: false}]}
                />
            </div>
        </div>
    );
};

export {CompanySelection};
